import {
  FC,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { IconButton, makeStyles } from "@material-ui/core";
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from "@material-ui/icons";
import { get, keyBy } from "lodash";
import { useQueryClient } from "react-query";
import { useHistory, useRouteMatch } from "react-router-dom";

import { LogInRequiredModal } from "components";
import { AppContext } from "contexts";
import * as routes from "routes";
import {
  favoriteListing,
  unFavoriteListing,
  updateFavoritedListing,
  useFavoritedListings,
} from "services/favoritedListings";
import { ACCEPTING_OFFERS_STATES } from "services/listings/constants";
import { ListingState } from "services/listings/types";
import theme from "theme";

import ReceiveNotificationsModal from "./ReceiveNotificationsModal";
import UnsubscribeModal from "./UnsubscribeModal";

const useStyles = makeStyles({
  root: {
    borderRadius: theme.shape.borderRadius,
    background: "rgba(255, 255, 255, .8)",
    transition: "background .3s ease-in-out",
    "&:hover": {
      background: theme.palette.background.default,
    },
    height: "34px",
    width: "34px",
  },
  noBackground: {
    background: "transparent",
    "&:hover": {
      background: "transparent",
    },
  },
  favoriteIcon: {
    color: theme.custom.palette.purple.main,
  },
});

interface FavoriteIconButtonProps {
  listingId: number;
  listingState: ListingState;
  removeBackground?: boolean;
  withBorder?: boolean;
  isAssetDetailsModal?: boolean;
}

const FavoriteIconButton: FC<FavoriteIconButtonProps> = ({
  listingId,
  listingState,
  removeBackground,
  withBorder,
  isAssetDetailsModal,
}) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const appContext = useContext(AppContext);
  const favoritedListingsQuery = useFavoritedListings();
  const history = useHistory();
  const isAssetDetail =
    useRouteMatch(routes.orderBookAssetDetail()) || isAssetDetailsModal;
  const urlSearchParams = new URLSearchParams(history.location.search);

  const userId = appContext.user?.userId;
  const favoritedListingsByListingId = !!favoritedListingsQuery?.data
    ? keyBy(
        favoritedListingsQuery.data,
        favoritedListing => favoritedListing.listing,
      )
    : {};
  const favoritedListing = get(favoritedListingsByListingId, listingId);
  const isPreviouslyFavoriteListing =
    !!favoritedListing &&
    !favoritedListing.has_presented_notification_enrollment_to_user;
  const isUnsubscribeParam = !!urlSearchParams.get("unsubscribe");

  const [isLogInRequiredModalOpen, setIsLogInRequiredModalOpen] = useState(
    false,
  );
  const [
    isReceiveNotificationsModalOpen,
    setIsReceiveNotificationsModalOpen,
  ] = useState(false);
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false);

  useEffect(() => {
    if (
      isAssetDetail &&
      ACCEPTING_OFFERS_STATES.includes(listingState) &&
      isPreviouslyFavoriteListing &&
      !isReceiveNotificationsModalOpen
    ) {
      setIsReceiveNotificationsModalOpen(true);
    }
    if (
      !!favoritedListing &&
      favoritedListing.has_presented_notification_enrollment_to_user &&
      isReceiveNotificationsModalOpen
    ) {
      setIsReceiveNotificationsModalOpen(false);
    }
  }, [
    isAssetDetail,
    favoritedListing,
    isReceiveNotificationsModalOpen,
    isPreviouslyFavoriteListing,
    listingState,
  ]);

  const handleFavoriteChangeSideEffects = useCallback(() => {
    queryClient.refetchQueries("favorited-listings");
    // this conditional call of `queryClient` is a workaround to prevent  resetting filters on BaseListings when this component is used on the AssetDetail modal see RXT-1523
    if (!isAssetDetailsModal) {
      queryClient.refetchQueries("inventory-listings");
    }
  }, [isAssetDetailsModal, queryClient]);

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (!userId) {
        setIsLogInRequiredModalOpen(true);
        return;
      }

      try {
        if (!!favoritedListing) {
          await unFavoriteListing(favoritedListing.id);
        } else {
          if (ACCEPTING_OFFERS_STATES.includes(listingState)) {
            setIsReceiveNotificationsModalOpen(true);
          } else {
            await favoriteListing(listingId, false);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        handleFavoriteChangeSideEffects();
      }
    },
    [
      userId,
      favoritedListing,
      handleFavoriteChangeSideEffects,
      listingId,
      listingState,
    ],
  );

  const handleFavorite = async (receiveNotifications: boolean) => {
    try {
      await favoriteListing(listingId, receiveNotifications);
      setIsReceiveNotificationsModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      handleFavoriteChangeSideEffects();
    }
  };

  const handleUpdateFavoritedListing = useCallback(
    async (receiveNotifications: boolean) => {
      try {
        await updateFavoritedListing(
          favoritedListing?.id,
          receiveNotifications,
        );
        handleFavoriteChangeSideEffects();
      } catch (error) {
        console.error(error);
      }
    },
    [favoritedListing?.id, handleFavoriteChangeSideEffects],
  );

  useEffect(() => {
    if (!!favoritedListingsQuery?.data) {
      if (
        isUnsubscribeParam &&
        !!favoritedListing &&
        favoritedListing.receive_notifications
      ) {
        handleUpdateFavoritedListing(false);
        history.push({ search: "" });
        setIsUnsubscribeModalOpen(true);
      }
      if (
        isUnsubscribeParam &&
        !favoritedListing?.receive_notifications &&
        !isUnsubscribeModalOpen
      ) {
        history.push({ search: "" });
      }
    }
  }, [
    favoritedListing,
    favoritedListingsQuery,
    handleUpdateFavoritedListing,
    history,
    isUnsubscribeModalOpen,
    isUnsubscribeParam,
  ]);

  return (
    <>
      {isLogInRequiredModalOpen && (
        <LogInRequiredModal
          open={isLogInRequiredModalOpen}
          onClose={() => setIsLogInRequiredModalOpen(false)}
        />
      )}
      {isReceiveNotificationsModalOpen && (
        <ReceiveNotificationsModal
          onClose={() => {
            if (!!favoritedListing) {
              handleUpdateFavoritedListing(false);
            } else {
              setIsReceiveNotificationsModalOpen(false);
            }
          }}
          onConfirm={
            !!favoritedListing
              ? (receiveNotifications: boolean) =>
                  handleUpdateFavoritedListing(receiveNotifications)
              : (receiveNotifications: boolean) =>
                  handleFavorite(receiveNotifications)
          }
          isPreviouslyFavoriteListing={isPreviouslyFavoriteListing}
        />
      )}
      {isUnsubscribeModalOpen && (
        <UnsubscribeModal
          onClose={() => {
            setIsUnsubscribeModalOpen(false);
          }}
        />
      )}
      <IconButton
        onClick={handleClick}
        className={removeBackground ? classes.noBackground : classes.root}
        style={{
          border: withBorder
            ? `2px solid ${theme.custom.palette.purple.main}`
            : "none",
        }}
        size="small"
      >
        {!!favoritedListing ? (
          <FavoriteIcon
            className={classes.favoriteIcon}
            data-testid="favorite-icon"
          />
        ) : (
          <FavoriteBorderIcon
            className={classes.favoriteIcon}
            data-testid="favorite-border-icon"
          />
        )}
      </IconButton>
    </>
  );
};

export default FavoriteIconButton;
